import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { contactService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import AuroraLoadingOverlay from '../components/AuroraLoadingOverlay';
import ChartDisplay from '../components/ChartDisplay';

const REL_LABEL: Record<string, string> = {
  romantico: '❤️ Romântico',
  amizade: '🤝 Amizade',
  familiar: '👨‍👩‍👧 Familiar',
  profissional: '💼 Profissional',
};

const TOKEN_COST = 1;

const KIND_CONFIG = {
  composto: {
    title: 'Mapa Composto',
    icon: '🌗',
    ctaTitle: 'Mapa Composto',
    description: 'Aurora Lyz vai calcular o ponto médio de cada planeta entre os dois mapas — a identidade da relação em si.',
    generatingMessage: 'Aurora está calculando o Mapa Composto...',
    generate: (id: number) => contactService.composto(id),
    getLatest: (id: number) => contactService.getLatestComposto(id),
  },
  davison: {
    title: 'Mapa de Davison',
    icon: '🕊️',
    ctaTitle: 'Mapa de Davison',
    description: 'Aurora Lyz vai calcular um mapa real no ponto médio de tempo e local entre os dois nascimentos.',
    generatingMessage: 'Aurora está calculando o Mapa de Davison...',
    generate: (id: number) => contactService.davison(id),
    getLatest: (id: number) => contactService.getLatestDavison(id),
  },
} as const;

function renderText(text: string): React.ReactNode {
  return text.split('\n').map((line, i) => {
    const parts: React.ReactNode[] = [];
    let last = 0;
    const boldRegex = /\*\*(.+?)\*\*/g;
    let match: RegExpExecArray | null;
    while ((match = boldRegex.exec(line)) !== null) {
      if (match.index > last) parts.push(line.slice(last, match.index));
      parts.push(<strong key={match.index} className="text-white font-semibold">{match[1]}</strong>);
      last = match.index + match[0].length;
    }
    if (last < line.length) parts.push(line.slice(last));
    if (line.trim() === '') return <div key={i} className="h-2" />;
    const isTitle = line.trim().startsWith('**') && parts.some(p => (p as any)?.type === 'strong');
    if (isTitle) return <h3 key={i} className="text-base font-semibold text-purple-300 mt-5 mb-2">{parts}</h3>;
    return <p key={i} className="text-white/80 text-sm leading-relaxed text-justify">{parts}</p>;
  });
}

interface RelationshipChartPageProps {
  kind: 'composto' | 'davison';
}

const RelationshipChartPage: React.FC<RelationshipChartPageProps> = ({ kind }) => {
  const cfg = KIND_CONFIG[kind];
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const [contact, setContact] = useState<any>(null);
  const [loadingContact, setLoadingContact] = useState(true);

  const [analysis, setAnalysis] = useState('');
  const [chartData, setChartData] = useState<any>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [errorAnalysis, setErrorAnalysis] = useState<string | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(true);

  useEffect(() => {
    contactService.list().then(list => {
      const found = list.find((c: any) => String(c.id) === contactId);
      setContact(found || null);
    }).catch(() => setContact(null)).finally(() => setLoadingContact(false));
  }, [contactId]);

  useEffect(() => {
    if (!contactId) return;
    cfg.getLatest(Number(contactId))
      .then(existing => {
        if (existing) {
          setAnalysis(existing.analysis);
          setChartData(existing.chart_data);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingExisting(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, kind]);

  const handleGenerate = async () => {
    if (!contactId) return;
    setLoadingAnalysis(true);
    setErrorAnalysis(null);
    try {
      const res = await cfg.generate(Number(contactId));
      setAnalysis(res.analysis);
      setChartData(res.chart_data);
      refresh().catch(() => {});
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setErrorAnalysis(detail || `Erro ao gerar ${cfg.title}.`);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const tokenBalance = (user as any)?.tokens_balance ?? 0;
  const noTokens = !((user as any)?.is_free) && !((user as any)?.is_admin) && tokenBalance < TOKEN_COST;

  const cardStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '1rem',
    padding: '2rem',
  };

  const btnPrimary = (disabled: boolean): React.CSSProperties => ({
    background: disabled ? 'rgba(139,92,246,0.3)' : 'linear-gradient(135deg, #7c3aed, #c026d3)',
    boxShadow: disabled ? 'none' : '0 0 20px rgba(139,92,246,0.35)',
  });

  return (
    <div className="min-h-screen bg-gradient-mystical theme-transition">
      <AuroraLoadingOverlay isLoading={loadingAnalysis} message={cfg.generatingMessage} />
      <div className="max-w-2xl mx-auto px-4 py-10">

        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate('/profile?tab=conexoes')}
            className="text-white/50 hover:text-white transition-colors text-sm"
          >
            ← Conexões
          </button>
          <h1 className="text-2xl font-bold text-white">{cfg.icon} {cfg.title}</h1>
        </div>

        {loadingContact ? (
          <p className="text-white/40 text-sm">Carregando...</p>
        ) : !contact ? (
          <p className="text-red-400 text-sm">Conexão não encontrada.</p>
        ) : (
          <>
            <div style={cardStyle} className="mb-6">
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #c026d3)' }}
                >
                  {contact.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xl font-semibold text-white">{contact.name}</span>
                    {contact.relationship_type && (
                      <span className="text-xs text-purple-300 bg-purple-500/15 px-2 py-0.5 rounded-full">
                        {REL_LABEL[contact.relationship_type] || contact.relationship_type}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-white/50">
                    {contact.birth_date?.slice(0, 10)}
                    {contact.birth_time ? ` às ${contact.birth_time.slice(0, 5)}` : ' (hora não informada)'}
                    {contact.birth_city ? ` · ${contact.birth_city}` : ''}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xs text-white/40">Saldo</div>
                  <div className={`text-sm font-bold ${noTokens ? 'text-red-400' : 'text-purple-300'}`}>
                    {tokenBalance} token{tokenBalance !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>

            {noTokens && (
              <div className="mb-6 px-4 py-3 rounded-lg text-sm bg-amber-500/15 text-amber-300 border border-amber-500/30">
                Você precisa de pelo menos {TOKEN_COST} token para gerar o {cfg.title}.{' '}
                <button onClick={() => navigate('/checkout')} className="underline font-semibold hover:text-amber-200">
                  Adquirir tokens →
                </button>
              </div>
            )}

            {loadingExisting ? (
              <div style={cardStyle} className="mb-6 text-center">
                <p className="text-white/40 text-sm">Verificando se já existe um {cfg.title} gerado...</p>
              </div>
            ) : !analysis && (
              <div style={cardStyle} className="mb-6 text-center">
                <div className="text-3xl mb-3">{cfg.icon}</div>
                <h2 className="text-white font-semibold text-lg mb-2">{cfg.ctaTitle}</h2>
                <p className="text-white/45 text-sm mb-6 leading-relaxed">
                  {cfg.description}<br/>
                  <span className="text-purple-400">Custo: {TOKEN_COST} token</span>
                </p>
                {errorAnalysis && (
                  <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-red-500/20 text-red-300 border border-red-500/30">
                    {errorAnalysis}
                  </div>
                )}
                <button
                  onClick={handleGenerate}
                  disabled={loadingAnalysis || noTokens}
                  className="px-8 py-4 rounded-xl font-semibold text-white text-base transition-all disabled:opacity-40"
                  style={btnPrimary(loadingAnalysis || noTokens)}
                >
                  {loadingAnalysis ? '✨ Calculando...' : `${cfg.icon} Gerar ${cfg.title}`}
                </button>
              </div>
            )}

            {analysis && (
              <>
                <div style={cardStyle} className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-white font-semibold">{cfg.title}</h2>
                    <button
                      onClick={handleGenerate}
                      disabled={loadingAnalysis || noTokens}
                      className="text-xs text-purple-300 hover:text-purple-200 disabled:opacity-40"
                    >
                      🔄 Gerar novo ({TOKEN_COST} token)
                    </button>
                  </div>
                  {errorAnalysis && (
                    <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-red-500/20 text-red-300 border border-red-500/30">
                      {errorAnalysis}
                    </div>
                  )}
                  <div>{renderText(analysis)}</div>
                </div>

                {chartData && (
                  <div style={cardStyle} className="mb-6">
                    <ChartDisplay chartData={chartData} />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RelationshipChartPage;
